import { useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts'

import {
	ChartContainer,
	ChartTooltip,
	ChartTooltipContent,
	type ChartConfig,
} from '@/components/ui/chart'
import { PageCard, PageLayout } from '@/components/ui/page'

import { useAuth } from '@/libs/hooks/use-auth'
import { abbreviateNumber } from '@/libs/utils/format'

import { salesReportChartQuery } from '@/features/sales/api'

export const Route = createFileRoute('/_app/report/sales/')({
	component: RouteComponent,
})

function RouteComponent() {
	return (
		<PageLayout.Root>
			<PageLayout.Header
				breadcrumbs={[
					['/report', 'Report'],
					['/report/sales', 'Laporan Penjualan'],
				]}
			/>
			<div className="grid gap-4 lg:grid-cols-2">
				<ChartNetSales />
				<ChartGrossSales />
				<ChartTransaction />
			</div>
		</PageLayout.Root>
	)
}

function ChartNetSales() {
	const { outletId } = useAuth((s) => s.outlet)
	const { isLoading, data } = useQuery(
		salesReportChartQuery({
			outletId,
			from: '2024-12-01',
			to: '2024-12-07',
		})
	)

	const chartConfig = useMemo(() => {
		return {
			val: {
				label: 'Net',
				color: 'var(--tag-blue-text)',
			},
		} satisfies ChartConfig
	}, [])

	const chartData = useMemo(() => {
		if (isLoading || !data?.result) return []
		return data.result.map((item) => ({
			month: item._id,
			val: item.net,
		}))
	}, [data])

	return (
		<>
			<PageCard.Root>
				<PageCard.Header title="Net Sales" />
				<PageCard.Content className="py-4">
					<ChartContainer config={chartConfig} className="aspect-[5/4] pr-2">
						<BarChart accessibilityLayer data={chartData}>
							<CartesianGrid vertical={false} />
							<XAxis
								height={60}
								dataKey="month"
								tickLine={false}
								angle={-45}
								textAnchor="end"
							/>
							<YAxis
								dataKey="val"
								tickLine
								axisLine
								// @ts-expect-error
								angle={-45}
								width={35}
								textAnchor="end"
								tickFormatter={(x) => abbreviateNumber(x as number) as string}
							/>
							<ChartTooltip
								cursor={false}
								content={<ChartTooltipContent indicator="dashed" />}
							/>
							<Bar
								dataKey="val"
								fill="var(--tag-blue-text)"
								radius={[5, 5, 0, 0]}
								maxBarSize={30}
							/>
						</BarChart>
					</ChartContainer>
				</PageCard.Content>
			</PageCard.Root>
		</>
	)
}

function ChartGrossSales() {
	const { outletId } = useAuth((s) => s.outlet)
	const { isLoading, data } = useQuery(
		salesReportChartQuery({
			outletId,
			from: '2024-12-01',
			to: '2024-12-07',
		})
	)

	const chartConfig = useMemo(() => {
		return {
			val: {
				label: 'Gross',
				color: 'var(--tag-blue-text)',
			},
		} satisfies ChartConfig
	}, [])

	const chartData = useMemo(() => {
		if (isLoading || !data?.result) return []
		return data.result.map((item) => ({
			month: item._id,
			val: item.gross,
		}))
	}, [data])

	return (
		<>
			<PageCard.Root>
				<PageCard.Header title="Gross Sales" />
				<PageCard.Content className="py-4">
					<ChartContainer config={chartConfig} className="aspect-[5/4]">
						<BarChart accessibilityLayer data={chartData}>
							<CartesianGrid vertical={false} />
							<XAxis
								height={60}
								dataKey="month"
								tickLine={false}
								angle={-45}
								textAnchor="end"
							/>
							<YAxis
								dataKey="val"
								tickLine
								axisLine
								tickFormatter={(x) => abbreviateNumber(x as number) as string}
							/>
							<ChartTooltip
								cursor={false}
								content={<ChartTooltipContent indicator="dashed" />}
							/>
							<Bar
								dataKey="val"
								fill="var(--tag-blue-text)"
								radius={[5, 5, 0, 0]}
								maxBarSize={30}
							/>
						</BarChart>
					</ChartContainer>
				</PageCard.Content>
			</PageCard.Root>
		</>
	)
}

function ChartTransaction() {
	const { outletId } = useAuth((s) => s.outlet)
	const { isLoading, data } = useQuery(
		salesReportChartQuery({
			outletId,
			from: '2024-12-01',
			to: '2024-12-07',
		})
	)

	const chartConfig = useMemo(() => {
		return {
			val: {
				label: 'Transaksi',
				color: 'var(--tag-blue-text)',
			},
		} satisfies ChartConfig
	}, [])

	const chartData = useMemo(() => {
		if (isLoading || !data?.result) return []
		return data.result.map((item) => ({
			month: item._id,
			val: item.trxTotal,
		}))
	}, [data])

	return (
		<>
			<PageCard.Root>
				<PageCard.Header title="Transaksi" />
				<PageCard.Content className="py-4">
					<ChartContainer config={chartConfig} className="aspect-[5/4] pr-2">
						<BarChart accessibilityLayer data={chartData}>
							<CartesianGrid vertical={false} />
							<XAxis
								height={60}
								dataKey="month"
								tickLine={false}
								angle={-45}
								textAnchor="end"
							/>
							<YAxis dataKey="val" tickLine axisLine width={35} />
							<ChartTooltip
								cursor={false}
								content={<ChartTooltipContent indicator="dashed" />}
							/>
							<Bar
								dataKey="val"
								fill="var(--tag-blue-text)"
								radius={[5, 5, 0, 0]}
								maxBarSize={30}
							/>
						</BarChart>
					</ChartContainer>
				</PageCard.Content>
			</PageCard.Root>
		</>
	)
}
